import React, { useState, useEffect } from 'react';
import { Users, RefreshCw, Search, Eye, Trash2, Edit, X } from 'lucide-react';
import { catalogService } from './services/catalogService';
import CreatorAuthorityForm from './CreatorAuthorityForm';

// Import your types from your actual types file
// import { CreatorAuthority } from '../types';

// Temporary type definitions
type CreatorRole = 
  | 'Author' | 'Writer' | 'Scribe' | 'Copyist' | 'Artist' | 'Illuminator'
  | 'Illustrator' | 'Printer' | 'Publisher' | 'Binder' | 'Engraver'
  | 'Etcher' | 'Cartographer' | 'Editor' | 'Translator' | 'Patron'
  | 'Former Owner' | 'Other';

interface CreatorAuthority {
  id: string;
  preferredName: string;
  primaryRole: CreatorRole;
  nameVariants?: string[];
  datesOfActivity?: string;
  birthDate?: string;
  deathDate?: string;
  additionalRoles?: CreatorRole[];
  nationality?: string[];
  placeOfBirth?: string;
  placeOfDeath?: string;
  biography?: string;
  viafId?: string;
  lcnafId?: string;
  wikidata?: string;
  createdDate: string;
  lastModified?: string;
}

const CreatorsListViewer: React.FC = () => {
  const [creators, setCreators] = useState<CreatorAuthority[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCreator, setSelectedCreator] = useState<CreatorAuthority | null>(null);
  const [editingCreator, setEditingCreator] = useState<CreatorAuthority | null>(null);
  const [error, setError] = useState('');

  const loadCreators = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await catalogService.getAllCreators();
      setCreators(data);
      console.log(`✅ Loaded ${data.length} creators from Firebase`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError('Failed to load creators: ' + errorMessage);
      console.error('❌ Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCreators();
  }, []);

  const filteredCreators = creators.filter(creator =>
    creator.preferredName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    creator.primaryRole?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (creatorId: string) => {
    if (!window.confirm('Are you sure you want to delete this creator?')) {
      return;
    }
    
    try {
      await catalogService.deleteCreator(creatorId);
      setCreators(creators.filter(c => c.id !== creatorId));
      setSelectedCreator(null);
      console.log('✅ Creator deleted');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError('Failed to delete: ' + errorMessage);
    }
  };

  const handleEdit = (creator: CreatorAuthority) => {
    setEditingCreator(creator);
    setSelectedCreator(null);
  };

  const handleSaveEdit = async (savedCreator: CreatorAuthority) => {
    // Refresh the creators list to show updated data
    await loadCreators();
    // Exit edit mode
    setEditingCreator(null);
    // Select the updated creator
    setSelectedCreator(savedCreator);
  };

  const handleCancelEdit = () => {
    setEditingCreator(null);
    setSelectedCreator(null);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Edit Mode - Show Form */}
      {editingCreator ? (
        <div className="bg-white rounded-lg shadow-lg">
          <div className="border-b p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Edit className="text-amber-600" size={28} />
                  Edit Creator: {editingCreator.preferredName}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Make changes and save to update the creator record
                </p>
              </div>
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 border rounded hover:bg-gray-50 flex items-center gap-2"
              >
                <X size={16} />
                Cancel
              </button>
            </div>
          </div>
          
          <div className="p-6">
            <CreatorAuthorityForm 
              initialCreator={editingCreator}
              onSave={handleSaveEdit}
            />
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-lg">
        {/* Header */}
        <div className="border-b p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Users className="text-amber-600" size={28} />
                Creator Authority Records
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {creators.length} creator{creators.length !== 1 ? 's' : ''} in database
              </p>
            </div>
            <button
              onClick={loadCreators}
              disabled={loading}
              className="px-4 py-2 border rounded hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw size={16} className={`inline mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or role..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-100 border border-red-400 text-red-800 rounded">
            {error}
          </div>
        )}

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 divide-x min-h-[600px]">
          {/* List Panel */}
          <div className="lg:col-span-1 overflow-y-auto max-h-[600px]">
            {loading ? (
              <div className="p-6 text-center text-gray-500">
                <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                Loading creators...
              </div>
            ) : filteredCreators.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                {searchTerm ? (
                  <>
                    <Search className="mx-auto mb-2" size={24} />
                    <p>No creators found matching "{searchTerm}"</p>
                  </>
                ) : (
                  <>
                    <Users className="mx-auto mb-2" size={24} />
                    <p>No creators in database yet.</p>
                    <p className="text-sm mt-2">Create one using the Creator Authority Form!</p>
                  </>
                )}
              </div>
            ) : (
              <div className="divide-y">
                {filteredCreators.map((creator) => (
                  <div
                    key={creator.id}
                    onClick={() => setSelectedCreator(creator)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition ${
                      selectedCreator?.id === creator.id ? 'bg-amber-50 border-l-4 border-amber-600' : ''
                    }`}
                  >
                    <p className="font-medium text-gray-900">{creator.preferredName}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {creator.primaryRole}
                    </p>
                    {creator.nationality && creator.nationality.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        {creator.nationality.join(', ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-2 p-6 overflow-y-auto max-h-[600px]">
            {selectedCreator ? (
              <div className="space-y-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {selectedCreator.preferredName}
                    </h2>
                    <p className="text-gray-600 mt-1">{selectedCreator.primaryRole}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(selectedCreator)}
                      className="px-3 py-2 border rounded hover:bg-gray-50"
                    >
                      <Edit size={16} className="inline mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(selectedCreator.id)}
                      className="px-3 py-2 border border-red-300 text-red-700 rounded hover:bg-red-50"
                    >
                      <Trash2 size={16} className="inline mr-1" />
                      Delete
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  {/* Basic Info */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Basic Information</h3>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-sm font-medium text-gray-700">ID</dt>
                        <dd className="text-sm text-gray-600 font-mono">{selectedCreator.id}</dd>
                      </div>
                      {selectedCreator.nameVariants && selectedCreator.nameVariants.length > 0 && (
                        <div>
                          <dt className="text-sm font-medium text-gray-700">Name Variants</dt>
                          <dd className="text-sm text-gray-600">
                            {selectedCreator.nameVariants.join(', ')}
                          </dd>
                        </div>
                      )}
                      {selectedCreator.datesOfActivity && (
                        <div>
                          <dt className="text-sm font-medium text-gray-700">Dates of Activity</dt>
                          <dd className="text-sm text-gray-600">{selectedCreator.datesOfActivity}</dd>
                        </div>
                      )}
                    </dl>
                  </div>

                  {/* Roles */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Roles</h3>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-sm font-medium text-gray-700">Primary Role</dt>
                        <dd className="text-sm text-gray-600">{selectedCreator.primaryRole}</dd>
                      </div>
                      {selectedCreator.additionalRoles && selectedCreator.additionalRoles.length > 0 && (
                        <div>
                          <dt className="text-sm font-medium text-gray-700">Additional Roles</dt>
                          <dd className="text-sm text-gray-600">
                            {selectedCreator.additionalRoles.join(', ')}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>
                </div>

                {/* Biography */}
                {selectedCreator.biography && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Biography</h3>
                    <p className="text-sm text-gray-700 leading-relaxed">{selectedCreator.biography}</p>
                  </div>
                )}

                {/* Geographic */}
                {(selectedCreator.placeOfBirth || selectedCreator.placeOfDeath || selectedCreator.nationality) && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Geographic Information</h3>
                    <dl className="space-y-2">
                      {selectedCreator.nationality && selectedCreator.nationality.length > 0 && (
                        <div>
                          <dt className="text-sm font-medium text-gray-700">Nationality</dt>
                          <dd className="text-sm text-gray-600">{selectedCreator.nationality.join(', ')}</dd>
                        </div>
                      )}
                      {selectedCreator.placeOfBirth && (
                        <div>
                          <dt className="text-sm font-medium text-gray-700">Place of Birth</dt>
                          <dd className="text-sm text-gray-600">{selectedCreator.placeOfBirth}</dd>
                        </div>
                      )}
                      {selectedCreator.placeOfDeath && (
                        <div>
                          <dt className="text-sm font-medium text-gray-700">Place of Death</dt>
                          <dd className="text-sm text-gray-600">{selectedCreator.placeOfDeath}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}

                {/* External IDs */}
                {(selectedCreator.viafId || selectedCreator.lcnafId || selectedCreator.wikidata) && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">External Identifiers</h3>
                    <dl className="space-y-2">
                      {selectedCreator.viafId && (
                        <div>
                          <dt className="text-sm font-medium text-gray-700">VIAF ID</dt>
                          <dd className="text-sm text-gray-600">{selectedCreator.viafId}</dd>
                        </div>
                      )}
                      {selectedCreator.lcnafId && (
                        <div>
                          <dt className="text-sm font-medium text-gray-700">LC NAF ID</dt>
                          <dd className="text-sm text-gray-600">{selectedCreator.lcnafId}</dd>
                        </div>
                      )}
                      {selectedCreator.wikidata && (
                        <div>
                          <dt className="text-sm font-medium text-gray-700">Wikidata</dt>
                          <dd className="text-sm text-gray-600">{selectedCreator.wikidata}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}

                {/* Metadata */}
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Metadata</h3>
                  <dl className="grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-700">Created</dt>
                      <dd className="text-sm text-gray-600">
                        {new Date(selectedCreator.createdDate).toLocaleString()}
                      </dd>
                    </div>
                    {selectedCreator.lastModified && (
                      <div>
                        <dt className="text-sm font-medium text-gray-700">Last Modified</dt>
                        <dd className="text-sm text-gray-600">
                          {new Date(selectedCreator.lastModified).toLocaleString()}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>

                {/* Raw JSON */}
                <details className="pt-4 border-t">
                  <summary className="cursor-pointer text-sm font-semibold text-gray-500 uppercase">
                    View Raw JSON
                  </summary>
                  <pre className="mt-2 p-4 bg-gray-50 rounded text-xs overflow-auto max-h-64">
                    {JSON.stringify(selectedCreator, null, 2)}
                  </pre>
                </details>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <Eye size={48} className="mx-auto mb-4" />
                  <p>Select a creator to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default CreatorsListViewer;